import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLogin, useVerifyMfa } from "@/hooks/useAuth";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});
const mfaSchema = z.object({
  code: z.string().min(1, "Enter your authenticator code"),
});

type LoginValues = z.infer<typeof loginSchema>;
type MfaValues = z.infer<typeof mfaSchema>;

function errMsg(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message ??
    "Something went wrong. Please try again."
  );
}

export default function LoginPage() {
  const [showPw, setShowPw] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const justRegistered = searchParams.get("registered") === "1";
  const forParent = searchParams.get("for") === "parent";

  const login = useLogin((token) => setMfaToken(token));
  const verifyMfa = useVerifyMfa();

  const loginForm = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
  });
  const mfaForm = useForm<MfaValues>({ resolver: zodResolver(mfaSchema) });

  const onLogin = (values: LoginValues) => login.mutate(values);
  const onMfa = (values: MfaValues) => {
    if (!mfaToken) return;
    verifyMfa.mutate({ mfaToken, code: values.code });
  };

  return (
    <AuthShell
      title={mfaToken ? "Two-step check" : "Sign in"}
      subtitle={
        mfaToken
          ? "Enter the code from your authenticator app."
          : forParent
            ? "Use the email you registered with as a parent."
            : "Teachers, school staff and parents"
      }
    >
        {!mfaToken && justRegistered && (
          <div className="mb-4 rounded-lg bg-ok-50 border border-ok-200 px-4 py-3 text-sm text-ok-700">
            Account created! Sign in below.
          </div>
        )}
        {!mfaToken && forParent && !justRegistered && (
          <div className="mb-4 rounded-lg bg-accent-50 border border-accent-200 px-4 py-3 text-sm text-accent-700">
            <strong>Parent login:</strong> use the email and password you
            registered with.
          </div>
        )}

        <div>
          {/* ── MFA step ── */}
          {mfaToken ? (
            <form
              onSubmit={mfaForm.handleSubmit(onMfa)}
              noValidate
              className="space-y-5"
            >
              <p className="text-sm text-portal-muted">
                Enter the 6-digit code from your authenticator app, or one of
                your backup codes.
              </p>
              <div>
                <label htmlFor="code" className="label">
                  Authentication code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000 000"
                  className={`input text-center tracking-widest text-lg ${mfaForm.formState.errors.code ? "border-alert-400" : ""}`}
                  {...mfaForm.register("code")}
                />
                {mfaForm.formState.errors.code && (
                  <p className="mt-1 text-xs text-alert-600">
                    {mfaForm.formState.errors.code.message}
                  </p>
                )}
              </div>

              {verifyMfa.isError && (
                <div className="rounded-lg bg-alert-50 border border-alert-200 px-4 py-3 text-sm text-alert-700">
                  {errMsg(verifyMfa.error)}
                </div>
              )}

              <button
                type="submit"
                disabled={verifyMfa.isPending}
                className="btn-primary w-full justify-center py-3 text-base"
              >
                {verifyMfa.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
                  </>
                ) : (
                  "Verify"
                )}
              </button>
              <button
                type="button"
                className="w-full text-center text-xs text-portal-muted hover:underline"
                onClick={() => setMfaToken(null)}
              >
                Back to sign-in
              </button>
            </form>
          ) : (
            /* ── Password step ── */
            <form
              onSubmit={loginForm.handleSubmit(onLogin)}
              noValidate
              className="space-y-5"
            >
              <div>
                <label htmlFor="email" className="label">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={`input ${loginForm.formState.errors.email ? "border-alert-400 focus:border-alert-400 focus:ring-alert-400" : ""}`}
                  placeholder={
                    forParent ? "parent@example.com" : "teacher@school.edu"
                  }
                  {...loginForm.register("email")}
                />
                {loginForm.formState.errors.email && (
                  <p className="mt-1 text-xs text-alert-600">
                    {loginForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label htmlFor="password" className="label mb-0">
                    Password
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-xs text-brand-blue hover:underline"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    className={`input pr-10 ${loginForm.formState.errors.password ? "border-alert-400 focus:border-alert-400 focus:ring-alert-400" : ""}`}
                    {...loginForm.register("password")}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center text-portal-muted hover:text-portal-text"
                    onClick={() => setShowPw(!showPw)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1 text-xs text-alert-600">
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {login.isError && (
                <div className="rounded-lg bg-alert-50 border border-alert-200 px-4 py-3 text-sm text-alert-700">
                  {errMsg(login.error)}
                </div>
              )}

              <button
                type="submit"
                disabled={login.isPending}
                className="btn-primary w-full justify-center py-3 text-base"
              >
                {login.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>
          )}
        </div>

        {!mfaToken && (
          <div className="mt-8 space-y-4 text-sm">
            <p className="text-portal-muted">
              New to Litlings?{" "}
              <Link to="/signup" className="font-semibold text-brand-blue hover:underline">
                Start a free trial
              </Link>
            </p>
            <Link
              to="/student/login"
              className="flex items-center justify-between rounded-[14px] bg-brand-yellow-soft px-4 py-3 font-semibold hover:bg-brand-yellow"
            >
              A child signing in? Go to the pupil sign-in
              <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        )}
    </AuthShell>
  );
}
